'use client'
import { useState } from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Check, X, Loader2, ArrowRight, AlertCircle, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LeadCaptureForm } from '@/components/marketing/lead-capture-form'

type AuditRow = {
  id: string
  label: string
  passed: boolean
  evidence: string
  gated: boolean
}

type AuditResult = {
  url: string
  finalUrl: string
  title: string
  score: number
  outOf: number
  rows: AuditRow[]
  fetchedAt: string
}

export function PdpAuditForm() {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<AuditResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [subscribed, setSubscribed] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!url.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    setSubscribed(false)
    try {
      const res = await fetch('/api/tools/pdp-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data?.error || 'Audit failed. Try a different URL.')
      } else {
        setResult(data as AuditResult)
      }
    } catch {
      setError('Network error. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const allRows = result?.rows ?? []

  const verdict = (() => {
    if (!result) return ''
    const pct = (result.score / result.outOf) * 100
    if (pct >= 80) return 'Strong PDP. Small tweaks would push it to the top decile.'
    if (pct >= 60) return 'Solid foundation. Three or four of the checks below would close the gap.'
    if (pct >= 40) return 'Half-built. The fixes below would meaningfully shift conversion and AI-citation rate.'
    return 'Significant gap to the top decile. Prioritise the failing rows below in order.'
  })()

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <Input
          type="url"
          inputMode="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste a product page URL (e.g. yourbrand.com/products/the-bag)"
          className="flex-1 h-12 text-base"
          aria-label="Product page URL"
        />
        <Button type="submit" size="lg" disabled={loading} className="h-12 px-6">
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Auditing
            </>
          ) : (
            <>
              Run audit <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      {loading && (
        <ToolProgress label="your product page" steps={[{ title: "Fetching your page", sub: "Loading the PDP HTML" }, { title: "Running the audit rubric", sub: "Conversion + trust signals" }, { title: "Scoring", sub: "Per-check pass / gap with evidence" }]} />
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-8 space-y-6">
          {/* Score gauge */}
          <div className="rounded-2xl border bg-card p-6 sm:p-8">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">PDP audit</p>
                <p className="mt-1 truncate font-display text-base font-medium" title={result.finalUrl}>
                  {result.title || result.finalUrl}
                </p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{result.finalUrl}</p>
              </div>
              <div className="flex shrink-0 items-baseline gap-2">
                <span className="font-display text-6xl font-bold tabular-nums text-foreground">
                  {result.score}
                </span>
                <span className="text-2xl text-muted-foreground">/ {result.outOf}</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-foreground/80 sm:text-base">{verdict}</p>
          </div>

          {/* All checks visible by default. Email gate sits below for
              the PDF report + weekly re-run extras, not for the rows. */}
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              All 8 checks
            </p>
            <ul className="mt-3 grid gap-3">
              {allRows.map((r) => (
                <li key={r.id} className="rounded-xl border bg-background p-4">
                  <div className="flex items-start gap-3">
                    <RowIcon passed={r.passed} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">{r.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{r.evidence}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Soft email gate for the extras: PDF report + weekly
              re-run. The audit itself stays fully visible above. */}
          {!subscribed ? (
            <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Mail className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-lg font-medium tracking-tight">
                    Want this in your inbox, weekly?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Drop your email. We will send a PDF report of this audit now, then re-run it every Monday and email you the diff until you tell us to stop.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <LeadCaptureForm
                  source={`/tools/pdp-audit`}
                  toolSlug="pdp-audit"
                  defaultIntent="other"
                  variant="compact"
                  onSuccess={() => setSubscribed(true)}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm">
              <p className="font-medium text-foreground">Subscribed. Look out for the PDF in the next few minutes, and the first weekly re-run next Monday.</p>
            </div>
          )}

          <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-xs text-muted-foreground">
            Audited {new Date(result.fetchedAt).toLocaleString()}. The score is a single-page snapshot, not a brand-wide grade. Pages render differently for different visitors, so a real merchant audit also looks at logged-in vs anonymous states, geography and device.
          </div>
        </div>
      )}
    </div>
  )
}

function RowIcon({ passed }: { passed: boolean }) {
  return passed ? (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
      <Check className="h-3 w-3" />
    </span>
  ) : (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300">
      <X className="h-3 w-3" />
    </span>
  )
}
